import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    MapPin,
    Calendar,
    ArrowRightLeft,
    MessageCircle,
    Building2,
    User,
    Check,
    ImageOff,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
} from "lucide-react";
import { formatPrice } from "@/lib/currency";
import { cityFallbackImage } from "@/lib/cityImages";
import RoomActionsMenu from "@/components/RoomActionsMenu";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function RoomDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [room, setRoom] = useState(null);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/rooms/${id}`);
                setRoom(data);
            } catch {
                toast.error("Room not found");
                navigate("/browse");
            }
        })();
    }, [id, navigate]);

    const contactOwner = () => {
        if (!user) { navigate("/login"); return; }
        navigate(`/messages/${room.owner_id}?room=${room.id}`);
    };

    if (!room) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="room-loading">
                <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
        );
    }

    const hasPhoto = Array.isArray(room.photos) && room.photos.length > 0;
    const cityFallback = cityFallbackImage(room.city);
    const photos = hasPhoto ? room.photos.map((p) => fileUrl(p)) : [cityFallback || FALLBACK_IMG];
    const canPrev = idx > 0;
    const canNext = idx < photos.length - 1;

    const goPrev = () => canPrev && setIdx(idx - 1);
    const goNext = () => canNext && setIdx(idx + 1);

    return (
        <div data-testid="room-detail-page" className="w-full overflow-x-hidden">
            {/* Mobile back pill — floats above the photo carousel on small screens */}
            <div className="md:hidden absolute top-[calc(env(safe-area-inset-top)+72px)] left-4 z-20">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="back-btn-mobile"
                    className="w-10 h-10 rounded-full bg-white/95 backdrop-blur shadow-md flex items-center justify-center active:scale-95 transition-transform"
                    aria-label="Back"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
            </div>
            {/* Mobile "•••" actions — Report / Block user (Apple UGC) */}
            <div className="md:hidden absolute top-[calc(env(safe-area-inset-top)+72px)] right-4 z-20">
                <RoomActionsMenu room={room} currentUser={user} />
            </div>

            {/* Mobile: edge-to-edge swipeable carousel */}
            <PhotoCarousel
                photos={photos}
                idx={idx}
                setIdx={setIdx}
                hasPhoto={hasPhoto}
                onPrev={goPrev}
                onNext={goNext}
                canPrev={canPrev}
                canNext={canNext}
            />

            {/* Content wrapper — constrained + safe padding, no horizontal overflow */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 pt-4 sm:pt-6">
                <Link
                    to="/browse"
                    className="hidden md:inline text-sm text-muted-foreground hover:text-foreground"
                    data-testid="back-to-browse"
                >
                    ← Back to browse
                </Link>

                <div className="grid md:grid-cols-12 gap-8 md:mt-4">
                    <div className="md:col-span-8 min-w-0">
                        {/* Desktop-only thumbnail row (mobile uses the carousel dots instead) */}
                        {photos.length > 1 && (
                            <div className="hidden md:flex gap-2 mt-3 overflow-x-auto scrollbar-hide">
                                {photos.map((p, i) => (
                                    <button
                                        key={i}
                                        data-testid={`photo-thumb-${i}`}
                                        onClick={() => setIdx(i)}
                                        className={`shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 ${i === idx ? "border-primary" : "border-transparent"}`}
                                    >
                                        <img loading="lazy" decoding="async" src={p} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}

                        <h1
                            className="font-display text-2xl sm:text-4xl font-bold mt-4 sm:mt-8 break-words"
                            data-testid="room-title"
                        >
                            {room.title}
                        </h1>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-2">
                            <span className="flex items-center gap-1 min-w-0">
                                <MapPin className="w-4 h-4 shrink-0" />
                                <span className="truncate">{room.city}{room.country ? `, ${room.country}` : ""}</span>
                            </span>
                            {room.university && (
                                <span className="flex items-center gap-1 min-w-0">
                                    <Building2 className="w-4 h-4 shrink-0" />
                                    <span className="truncate">{room.university}</span>
                                </span>
                            )}
                        </div>

                        <div
                            className="mt-6 text-foreground/90 leading-relaxed whitespace-pre-wrap break-words"
                            data-testid="room-description"
                        >
                            {room.description}
                        </div>

                        {room.amenities && room.amenities.length > 0 && (
                            <div className="mt-8">
                                <h3 className="font-display text-lg font-semibold">Amenities</h3>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {room.amenities.map((a) => (
                                        <Badge key={a} variant="secondary" className="rounded-full px-3 py-1 bg-muted text-foreground border-0">
                                            <Check className="w-3 h-3 mr-1 text-secondary" /> {a}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <aside className="md:col-span-4 min-w-0">
                        <div className="bg-white border border-border/60 rounded-2xl p-5 sm:p-6 md:sticky md:top-24">
                            <div className="flex items-baseline gap-1 flex-wrap">
                            <span className="font-display text-3xl sm:text-4xl font-bold">
                                {formatPrice(room.rent_per_month, room.currency || "EUR")}
                            </span>
                            <span className="text-muted-foreground">/month</span>
                            <div className="ml-auto hidden md:block">
                                <RoomActionsMenu room={room} currentUser={user} />
                            </div>
                        </div>
                            <div className="mt-5 space-y-3">
                                <InfoRow icon={Calendar} title="Available" body={`${room.available_from} → ${room.available_to}`} />
                                {room.destination_city && (
                                    <InfoRow
                                        icon={ArrowRightLeft}
                                        title={`${room.owner_name} is going to`}
                                        body={`${room.destination_city}${room.destination_country ? `, ${room.destination_country}` : ""}`}
                                    />
                                )}
                                <InfoRow icon={User} title="Host" body={room.owner_name} />
                            </div>

                            {user && user.id !== room.owner_id ? (
                                <Button
                                    data-testid="contact-owner-btn"
                                    onClick={contactOwner}
                                    className="w-full mt-6 rounded-full h-11 bg-primary hover:bg-primary/90"
                                >
                                    <MessageCircle className="w-4 h-4 mr-1" /> Send message
                                </Button>
                            ) : !user ? (
                                <Button
                                    data-testid="login-to-contact-btn"
                                    onClick={() => navigate("/login")}
                                    className="w-full mt-6 rounded-full h-11 bg-primary hover:bg-primary/90"
                                >
                                    Log in to contact
                                </Button>
                            ) : (
                                <div className="mt-6 text-sm text-muted-foreground text-center" data-testid="own-room-note">
                                    This is your room.
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

function InfoRow({ icon: Icon, title, body }) {
    return (
        <div className="flex items-start gap-2 text-sm min-w-0">
            <Icon className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div className="min-w-0">
                <div className="font-medium">{title}</div>
                <div className="text-muted-foreground break-words">{body}</div>
            </div>
        </div>
    );
}

/**
 * Swipeable, full-width photo carousel.
 * - Mobile: edge-to-edge, drag to swipe, dots below.
 * - Desktop: contained aspect-ratio hero with chevron buttons.
 * Never overflows the viewport horizontally.
 */
function PhotoCarousel({ photos, idx, setIdx, hasPhoto, onPrev, onNext, canPrev, canNext }) {
    const containerRef = useRef(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const measure = () => {
            if (containerRef.current) setWidth(containerRef.current.clientWidth);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    const handleDragEnd = (_, info) => {
        const threshold = 60;
        if (info.offset.x < -threshold && canNext) onNext();
        else if (info.offset.x > threshold && canPrev) onPrev();
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full bg-muted overflow-hidden md:mt-6 md:rounded-2xl md:max-w-7xl md:mx-auto md:px-0"
        >
            <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden">
                <AnimatePresence initial={false} mode="popLayout">
                    <motion.img
                        key={idx}
                        src={photos[idx]}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        drag={photos.length > 1 ? "x" : false}
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.2}
                        onDragEnd={handleDragEnd}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className={`absolute inset-0 w-full h-full object-cover select-none ${hasPhoto ? "" : "opacity-40 grayscale contrast-75"}`}
                        onError={(e) => {
                            const el = e.currentTarget;
                            if (el.src !== FALLBACK_IMG) el.src = FALLBACK_IMG;
                        }}
                        draggable={false}
                    />
                </AnimatePresence>

                {!hasPhoto && (
                    <div
                        data-testid="room-detail-nophoto"
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                        <div className="bg-foreground/80 text-background px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.25em] inline-flex items-center gap-2 backdrop-blur-sm">
                            <ImageOff className="w-3.5 h-3.5" />
                            No photo uploaded
                        </div>
                    </div>
                )}

                {photos.length > 1 && (
                    <>
                        {/* Desktop chevrons */}
                        <button
                            onClick={onPrev}
                            disabled={!canPrev}
                            aria-label="Previous photo"
                            data-testid="carousel-prev"
                            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow items-center justify-center disabled:opacity-0 hover:bg-white transition"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onNext}
                            disabled={!canNext}
                            aria-label="Next photo"
                            data-testid="carousel-next"
                            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow items-center justify-center disabled:opacity-0 hover:bg-white transition"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>

                        {/* Photo counter pill */}
                        <div className="absolute bottom-3 right-3 bg-black/55 text-white text-xs font-semibold px-2.5 py-1 rounded-full backdrop-blur">
                            {idx + 1} / {photos.length}
                        </div>
                    </>
                )}
            </div>

            {/* Dots (mobile-first, also nice on desktop) */}
            {photos.length > 1 && (
                <div className="flex justify-center gap-1.5 py-3" data-testid="carousel-dots">
                    {photos.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setIdx(i)}
                            aria-label={`Go to photo ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/30"}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
